import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useEstimator } from './context';
import EstimatorHeader from './EstimatorHeader';
import ServicesStep from './ServicesStep';
import EstimateStep from './EstimateStep';
import styles from './ProjectEstimator.module.scss';

export const ProjectEstimator: React.FC = () => {
  const { isOpen, closeEstimator, step, nextStep, prevStep, canProceedToNext, estimate } =
    useEstimator();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeEstimator();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeEstimator]);

  // Scroll to top of body when step changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          role="dialog"
          aria-modal="true"
          aria-label="Arkatechs Project Estimator"
        >
          {/* Header */}
          <EstimatorHeader />

          {/* Scrollable Content Body */}
          <div className={styles.body} ref={scrollContainerRef}>
            <div className={styles.contentWrapper}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                >
                  {step === 1 && <ServicesStep />}
                  {step === 2 && <EstimateStep />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom Fixed / Sticky Controls Bar */}
          <footer className={styles.footer}>
            <div className={styles.footerInner}>
              {step === 1 ? (
                <>
                  <div className={styles.footerSummary}>
                    <span className={styles.footerSummaryLabel}>STARTING ESTIMATE</span>
                    <span className={styles.footerSummaryValue}>
                      {estimate.selectedCount > 0 ? estimate.formattedTotal : '₹0'}
                    </span>
                  </div>

                  <button
                    type="button"
                    className={styles.nextBtn}
                    onClick={nextStep}
                    disabled={!canProceedToNext}
                    id="estimator-view-estimate-btn"
                  >
                    <span>VIEW ESTIMATE</span>
                    <ArrowRight size={16} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.backBtn}
                    onClick={prevStep}
                    id="estimator-back-to-services-btn"
                  >
                    <ArrowLeft size={16} />
                    <span>MODIFY SERVICES</span>
                  </button>

                  <div className={styles.footerEstimateRight}>
                    <span className={styles.footerSummaryLabel}>STARTING FROM</span>
                    <span className={styles.footerSummaryValue}>{estimate.formattedTotal}</span>
                  </div>
                </>
              )}
            </div>
          </footer>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectEstimator;
